import api from '@/services/Api';
import Impfstoff from '@/types/Impfstoffe';
import { successSwal, warningSwal } from '@/helper/swals';
import { ActionContext, Module } from 'vuex';
import { AxiosResponse } from 'axios';
import { ImpfstoffeLoaders, ImpfstoffeState, RootState } from '@/store/types';
import { DataResponse } from '@/types';
import { Teilimpfungsart } from '@/types/Teilimpfungsart';
import Impfdosis from '@/types/Impfdosis';
import moment from 'moment';
import { saveAs } from 'file-saver';

export type ImpfstoffeFilter = {
    gueltig: string;
};

export type LoadImpfstoffePayload = {
    filter: ImpfstoffeFilter;
    teilimpfungsarten?: boolean
    impfdosen?: boolean;
};

export type ExportImpfstoffePayload = {
    filter: ImpfstoffeFilter;
};

export type RefreshImpfabstaendePayload = {
    impfstoff: Impfstoff;
};

export type UpdateImpfstoffPayload = {
    impfstoff: Impfstoff;
    filter: ImpfstoffeFilter;
};

export type DeleteImpfstoffPayload = {
    impfstoff: Impfstoff;
    filter: ImpfstoffeFilter;
};

export type LoadImpfstoffeData = DataResponse<Partial<Impfstoff>, {
   teilimpfungsarten?: Teilimpfungsart[];
   impfdosen?: Impfdosis[];
}>;

type Context = ActionContext<ImpfstoffeState, RootState>;

export const impfstoffe: Module<ImpfstoffeState, RootState> = {
    namespaced: true,
    state: {
        dialogs: {
            edit: false,
        },
        loaders: {
            save: false,
            delete: false,
            impfstoffe: false,
            export: false,
            impfabstaende: false,
        },
        impfstoffe: [],
    },
    actions: {
        loadImpfstoffe({ commit, rootState }: Context, payload?: LoadImpfstoffePayload): void {
            commit('setLoadersImpfstoffe', true);
            commit('setImpfstoffe', []);

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('verwaltung/impfstoffe/load', data)
                .then(({ data: { body, selections } }: AxiosResponse<LoadImpfstoffeData>) => {
                    commit('setImpfstoffe', body);

                    if (payload?.teilimpfungsarten) {
                        commit('impfstoff/setTeilimpfungsarten', selections.teilimpfungsarten, { root: true });
                    }
                    if (payload?.impfdosen) {
                        commit('impfstoff/setImpfdosen', selections.impfdosen, { root: true });
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersImpfstoffe', false));
        },
        updateImpfstoff({ commit, dispatch }: Context, { impfstoff, filter }: UpdateImpfstoffPayload): Promise<void> {
            commit('setLoadersSave', true);

            return api.post('verwaltung/impfstoffe/speichern', impfstoff)
                .then(() => {
                    successSwal('Impfstoff', 'gespeichert');
                    dispatch('loadImpfstoffe', { filter });
                })
                .catch(console.error)
                .finally(() => commit('setLoadersSave', false));
        },
        deleteImpfstoff({ commit, dispatch }: Context, { impfstoff, filter }: DeleteImpfstoffPayload): void {
            commit('setLoadersDelete', true);

            api.post('verwaltung/impfstoffe/loeschen', impfstoff)
                .then((response: AxiosResponse) => {
                    if (typeof response.data.body !== 'string') {
                        successSwal('Impfstoff', 'gelöscht');
                        dispatch('loadImpfstoffe', { filter });
                        commit('setDialogsDetail', false);
                    } else {
                        warningSwal('Warnung', response.data.body ?? 'Fehler beim Löschen.');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoadersDelete', false));
        },
        exportImpfstoffe({ commit, rootState, rootGetters }: Context, payload?: ExportImpfstoffePayload): void {
            commit('setLoaders', { export: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                ...payload,
            };

            api.post('verwaltung/impfstoffe/export', data, { responseType: 'blob' })
                .then((response: AxiosResponse) => {
                    const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                    const impfstelle = rootGetters['authentication/getImpfstelleName'].replace(/ /g, '_');
                    saveAs(response.data, `Impfstoffe_${impfstelle}_${date}.xlsx`);
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { export: false }));
        },
        refreshImpfabstaende({ commit, rootState, rootGetters }: Context,
                             { impfstoff }: RefreshImpfabstaendePayload): void {
            commit('setLoaders', { impfabstaende: true });

            const data = {
                impfstelle_id: rootState.authentication.impfstelle_id,
                impfstoff_id: impfstoff.impfstoff_id,
            };

            const isExport = rootGetters['authentication/isAllowed']('system', 'index');

            api.post('verwaltung/impfstoffe/impfabstaende', data, isExport ? { responseType: 'blob' } : undefined)
                .then((response: AxiosResponse) => {
                    if (isExport) {
                        const date = moment().format('DD-MM-YYYY_HH-mm-ss');
                        const impfstoffImpfstoff = impfstoff.impfstoff?.replace(/ /g, '_') ?? '';
                        saveAs(response.data, `Impfabstaende_Aktualisierungen_${impfstoffImpfstoff}_${date}.xlsx`);
                    } else {
                        successSwal('Impfabstände', response.data?.body?.toString() ?? 'aktualisiert');
                    }
                })
                .catch(console.error)
                .finally(() => commit('setLoaders', { impfabstaende: false }));
        },
    },
    mutations: {
        setImpfstoffe(state: ImpfstoffeState, payload: Partial<Impfstoff>[]): void {
            state.impfstoffe = payload.map((data) => new Impfstoff(data));
        },
        setLoaders(state: ImpfstoffeState, loaders: ImpfstoffeLoaders): void {
            state.loaders = {
                ...state.loaders,
                ...loaders,
            };
        },
        setDialogsDetail(state: ImpfstoffeState, visible: boolean): void {
            state.dialogs.edit = visible;
        },
        setLoadersSave(state: ImpfstoffeState, visible: boolean): void {
            state.loaders.save = visible;
        },
        setLoadersDelete(state: ImpfstoffeState, visible: boolean): void {
            state.loaders.delete = visible;
        },
        setLoadersImpfstoffe(state: ImpfstoffeState, visible: boolean): void {
            state.loaders.impfstoffe = visible;
        },
    },
};
